import React, {FC, FormEvent, useEffect, useRef, useState} from 'react';
import {
  ApiOwnerAccount,
  ApiOwnerWishlist,
  requestOwnerLogin,
  verifyOwnerLogin,
} from '../souhait-api';
import {
  Badge,
  Brand,
  Button,
  Heading,
  Icon,
  Motif,
  Paper,
  Select,
  Text,
  TurnstileWidget,
} from '../ui';
import './CommercialPages.css';

type Navigate = (path: string) => void;

export const formatAccessDate = (value: string) => {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString('fr-FR', {day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Paris'})
    : 'date à confirmer';
};

export const BillingReturnNotice: FC<{
  account: ApiOwnerAccount;
  wishlists: ApiOwnerWishlist[];
  onRefresh: () => Promise<void>;
}> = ({account, wishlists, onRefresh}) => {
  const query = new URLSearchParams(window.location.search);
  const isReturn = query.get('billing') === 'success';
  const offer = query.get('offer');
  const passList = wishlists.find(list => list.id === query.get('list'));
  const confirmed = offer === 'pass'
    ? Boolean(passList && passList.canCollect)
    : ['monthly', 'annual'].includes(offer || '') && account.plan === 'plus';
  const [timedOut, setTimedOut] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  useEffect(() => {
    if (!isReturn || confirmed || timedOut) return;
    let cancelled = false;
    let attempts = 0;
    let timer = 0;
    const refresh = async () => {
      try {
        await onRefresh();
      } catch {
        if (!cancelled) setRefreshError('La vérification est momentanément indisponible.');
      }
      attempts += 1;
      if (cancelled) return;
      if (attempts >= 15) setTimedOut(true);
      else timer = window.setTimeout(refresh, 2000);
    };
    timer = window.setTimeout(refresh, 2000);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [isReturn, confirmed, timedOut, onRefresh]);
  if (!isReturn) return null;
  return (
    <section className="billing-return" role="status" aria-live="polite">
      <strong>{confirmed ? 'Vos droits sont actifs.' : 'Vérification de votre paiement…'}</strong>
      <p>
        {confirmed
          ? offer === 'pass' && passList
            ? `${passList.title} est débloquée jusqu’au ${formatAccessDate(passList.passExpiresAt)}. Le Pass ne se renouvelle pas automatiquement.`
            : 'Plus est actif. Vous pouvez créer plusieurs listes et ajouter des souhaits sans limite.'
          : timedOut
          ? 'La confirmation n’est pas encore arrivée. Ne repayez pas : actualisez vos droits ou écrivez à support@souha.it avec votre reçu Lemon Squeezy.'
          : 'Lemon Squeezy confirme le paiement avant l’ouverture des droits. Cela peut prendre quelques instants.'}
      </p>
      {refreshError && !confirmed && <p>{refreshError}</p>}
      {timedOut && !confirmed && (
        <button type="button" onClick={() => { setRefreshError(''); setTimedOut(false); }}>
          Actualiser mes droits
        </button>
      )}
    </section>
  );
};

const CommercialHeader: FC<{navigate: Navigate}> = ({navigate}) => (
  <header className="commercial-nav">
    <a
      href="/"
      aria-label="Accueil souha.it"
      onClick={event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        navigate('/');
      }}
    >
      <Brand />
    </a>
    <nav aria-label="Navigation principale">
      <a
        href="/pricing"
        onClick={event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          navigate('/pricing');
        }}
      >
        Tarifs
      </a>
      <a
        href="/me"
        onClick={event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          navigate('/me');
        }}
      >
        Mes listes
      </a>
    </nav>
  </header>
);

const CommercialFooter: FC<{navigate: Navigate}> = ({navigate}) => (
  <footer className="commercial-footer">
    <Brand />
    <nav aria-label="Informations légales">
      {[
        ['/pricing', 'Tarifs'],
        ['/privacy', 'Confidentialité'],
        ['/terms', 'Conditions'],
      ].map(([path, label]) => (
        <a
          href={path}
          key={path}
          onClick={event => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            navigate(path);
          }}
        >
          {label}
        </a>
      ))}
    </nav>
    <Text size="sm" tone="muted">Paris · 2026</Text>
  </footer>
);

export const OwnerLoginPage: FC<{
  navigate: Navigate;
  onAuthenticated: (account: ApiOwnerAccount) => Promise<void>;
}> = ({navigate, onAuthenticated}) => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [developmentCode, setDevelopmentCode] = useState('');
  const mounted = useRef(true);

  useEffect(() => {
    document.title = 'Créer une liste | souha.it';
    return () => {
      mounted.current = false;
    };
  }, []);

  const submitEmail = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await requestOwnerLogin(email, turnstileToken);
      if (mounted.current) {
        setDevelopmentCode(result.developmentCode || '');
        setStep('code');
      }
    } catch (requestError) {
      if (mounted.current) {
        setTurnstileToken('');
        setTurnstileAttempt(current => current + 1);
        setError(
          requestError instanceof Error
            ? requestError.message
            : 'Le code ne peut pas être envoyé pour le moment.'
        );
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const submitCode = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await verifyOwnerLogin(email, code);
      await onAuthenticated(result.account);
    } catch (requestError) {
      if (mounted.current) {
        setError(
          requestError instanceof Error
            ? requestError.message
            : 'Ce code ne peut pas être vérifié.'
        );
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  return (
    <main className="commercial-page commercial-auth">
      <CommercialHeader navigate={navigate} />
      <section className="commercial-auth__layout">
        <div className="commercial-auth__copy">
          <Badge tone="sky" icon="lock">Votre espace privé</Badge>
          <Heading display level={1}>
            Une liste en une minute. Aucun mot de passe.
          </Heading>
          <Text size="lg" tone="muted">
            Recevez un code par e-mail, créez votre première liste, puis collez
            le lien du cadeau qui compte.
          </Text>
          <div className="commercial-auth__facts">
            <span><Icon name="heart" size={18} /> Une liste gratuite</span>
            <span><Icon name="users" size={18} /> Invités sans compte</span>
            <span><Icon name="gift" size={18} /> Réserver ou participer</span>
          </div>
        </div>
        <Paper variant="raised" className="commercial-auth__paper">
          {step === 'email' ? (
            <form onSubmit={submitEmail}>
              <Badge tone="terracotta" icon="link">Étape 1 sur 2</Badge>
              <Heading level={2}>Ouvrir mon espace</Heading>
              <Text tone="muted">
                Nous vous envoyons un code à six chiffres valable dix minutes.
              </Text>
              <label htmlFor="owner-email">
                Votre e-mail
                <input
                  id="owner-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  placeholder="vous@exemple.fr"
                  required
                  autoFocus
                />
              </label>
              <TurnstileWidget
                key={turnstileAttempt}
                onToken={setTurnstileToken}
              />
              {error && <p className="commercial-error" role="alert">{error}</p>}
              <Button
                fullWidth
                size="lg"
                type="submit"
                loading={loading}
                disabled={!email.includes('@') || !turnstileToken}
                iconEnd="arrow-right"
              >
                Recevoir mon code
              </Button>
            </form>
          ) : (
            <form onSubmit={submitCode}>
              <Badge tone="leaf" icon="check">Code envoyé</Badge>
              <Heading level={2}>Consultez votre e-mail</Heading>
              <Text tone="muted">
                Saisissez le code envoyé à <strong>{email}</strong>.
              </Text>
              {developmentCode && (
                <p className="commercial-dev-code" role="status">
                  Code local : <strong>{developmentCode}</strong>
                </p>
              )}
              <label htmlFor="owner-code">
                Code à six chiffres
                <input
                  id="owner-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9 ]{6,8}"
                  value={code}
                  onChange={event => setCode(event.target.value)}
                  placeholder="123 456"
                  required
                  autoFocus
                />
              </label>
              {error && <p className="commercial-error" role="alert">{error}</p>}
              <Button
                fullWidth
                size="lg"
                type="submit"
                loading={loading}
                disabled={code.replace(/\D/g, '').length !== 6}
                iconEnd="arrow-right"
              >
                Continuer
              </Button>
              <button
                className="commercial-link-button"
                type="button"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setTurnstileToken('');
                }}
              >
                Utiliser une autre adresse
              </button>
            </form>
          )}
        </Paper>
      </section>
      <CommercialFooter navigate={navigate} />
    </main>
  );
};

export const PricingPage: FC<{
  navigate: Navigate;
  account: ApiOwnerAccount | null;
  wishlists: ApiOwnerWishlist[];
  onCheckout: (
    kind: 'pass' | 'monthly' | 'annual',
    wishlistId?: string
  ) => Promise<void>;
  onPortal: () => Promise<void>;
}> = ({navigate, account, wishlists, onCheckout, onPortal}) => {
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');
  const [errorOffer, setErrorOffer] = useState('');
  const errorNotice = useRef<HTMLParagraphElement | null>(null);
  const [selectedListId, setSelectedListId] = useState(
    new URLSearchParams(window.location.search).get('list') || ''
  );
  useEffect(() => {
    document.title = 'Tarifs simples | souha.it';
  }, []);
  useEffect(() => {
    if (error && errorNotice.current && errorNotice.current.scrollIntoView) {
      errorNotice.current.scrollIntoView({block: 'nearest', behavior: 'auto'});
    }
  }, [error]);
  const act = async (
    key: string,
    action: () => Promise<void>
  ) => {
    setLoading(key);
    setError('');
    try {
      await action();
    } catch (actionError) {
      setErrorOffer(key);
      setError(
        actionError instanceof Error
          ? actionError.message
          : 'La facturation est momentanément indisponible.'
      );
      setLoading('');
    }
  };
  const selectedList = wishlists.find(list => list.id === selectedListId) || wishlists[0];
  const alreadyEntitled = selectedList && selectedList.plan !== 'free';
  const needsAccount = (offer: string) => navigate(`/me?offer=${offer}`);

  return (
    <main className="commercial-page pricing-page">
      <CommercialHeader navigate={navigate} />
      <section className="pricing-hero">
        <div>
          <Heading display level={1}>
            Commencez gratuitement. Payez seulement quand la liste grandit.
          </Heading>
          <Text size="lg" tone="muted">
            Réserver et promettre une participation restent gratuits. Les
            offres payantes ajoutent les souhaits illimités et le relais vers
            votre moyen de paiement externe. Plus permet aussi plusieurs listes.
          </Text>
        </div>
        <Motif name="gift" size={150} />
      </section>

      <section className="pricing-grid" aria-label="Offres souha.it">
        <Paper variant="flat" className="pricing-plan">
          <Badge tone="sky" icon="heart">Pour commencer</Badge>
          <Heading level={2}>Gratuit</Heading>
          <p className="pricing-plan__price"><strong>0€</strong><span>pour toujours</span></p>
          <ul>
            <li><Icon name="check" size={17} />1 liste active</li>
            <li><Icon name="check" size={17} />Jusqu’à 10 souhaits</li>
            <li><Icon name="check" size={17} />Réservations privées</li>
            <li><Icon name="check" size={17} />Promesses de participation</li>
          </ul>
          <Button
            variant="paper"
            fullWidth
            size="lg"
            onClick={() => navigate('/me')}
          >
            {account ? 'Ouvrir mes listes' : 'Créer ma liste'}
          </Button>
        </Paper>

        <Paper variant="raised" className="pricing-plan pricing-plan--pass">
          <Badge tone="mustard" icon="gift">Une grande occasion</Badge>
          <Heading level={2}>Pass occasion</Heading>
          <p className="pricing-plan__price"><strong>29€</strong><span>une fois · 12 mois</span></p>
          <ul>
            <li><Icon name="check" size={17} />1 liste, souhaits illimités</li>
            <li><Icon name="check" size={17} />Relais vers votre paiement externe</li>
            <li><Icon name="check" size={17} />Suivi payé / à confirmer</li>
            <li><Icon name="check" size={17} />Aucun pourcentage souha.it sur les cadeaux</li>
          </ul>
          <p className="pricing-plan__detail">
            Douze mois à partir de l’achat, pour la liste choisie. Aucun
            renouvellement automatique. À l’échéance, les souhaits restent
            visibles ; les ajouts au-delà de dix et le relais de paiement s’arrêtent.
          </p>
          {account && selectedList && (
            <div className="pricing-plan__selection">
              <label id="pass-wishlist-label" htmlFor="pass-wishlist">Liste à débloquer</label>
              <Select id="pass-wishlist" aria-labelledby="pass-wishlist-label" value={selectedList.id}
                onValueChange={setSelectedListId}
                options={wishlists.map(list => ({value: list.id, label: list.title}))} />
            </div>
          )}
          <Button
            variant="secondary"
            fullWidth
            size="lg"
            loading={loading === 'pass'}
            disabled={Boolean(loading) || Boolean(alreadyEntitled)}
            onClick={() =>
              account && selectedList
                ? act('pass', () => onCheckout('pass', selectedList.id))
                : needsAccount('pass')
            }
          >
            {alreadyEntitled
              ? 'Cette liste est déjà débloquée'
              : account && !selectedList
              ? 'Créer d’abord la liste'
              : 'Débloquer pour 29€'}
          </Button>
          {error && errorOffer === 'pass' && <p ref={errorNotice} className="pricing-error" role="alert">{error}</p>}
        </Paper>

        <Paper variant="tinted" className="pricing-plan pricing-plan--plus">
          <Badge tone="leaf" icon="users">Pour les organisateurs</Badge>
          <Heading level={2}>Plus</Heading>
          <p className="pricing-plan__price"><strong>39€</strong><span>par an</span></p>
          <ul>
            <li><Icon name="check" size={17} />Listes et souhaits illimités</li>
            <li><Icon name="check" size={17} />Relais de paiement sur chaque liste</li>
            <li><Icon name="check" size={17} />Toutes les occasions au même endroit</li>
            <li><Icon name="check" size={17} />Annulation depuis le portail Lemon Squeezy</li>
          </ul>
          <p className="pricing-plan__detail">
            Renouvellement automatique : 39€ chaque année ou 5€ chaque mois,
            selon votre choix. Annulez à tout moment ; l’accès reste actif
            jusqu’à la fin de la période payée.
          </p>
          {account && account.plan === 'plus' ? (
            <Button
              variant="paper"
              fullWidth
              size="lg"
              loading={loading === 'portal'}
              onClick={() => act('portal', onPortal)}
            >
              Gérer mon abonnement
            </Button>
          ) : (
            <>
              <Button
                fullWidth
                size="lg"
                loading={loading === 'annual'}
                disabled={Boolean(loading)}
                onClick={() =>
                  account
                    ? act('annual', () => onCheckout('annual'))
                    : needsAccount('annual')
                }
              >
                Choisir Plus annuel
              </Button>
              <button
                className="pricing-plan__monthly"
                type="button"
                disabled={Boolean(loading)}
                onClick={() =>
                  account
                    ? act('monthly', () => onCheckout('monthly'))
                    : needsAccount('monthly')
                }
              >
                ou 5€ par mois
              </button>
            </>
          )}
          {error && errorOffer !== 'pass' && <p ref={errorNotice} className="pricing-error" role="alert">{error}</p>}
        </Paper>
      </section>
      <section className="pricing-trust">
        <Heading level={2}>L’argent des cadeaux ne passe jamais par souha.it.</Heading>
        <Text tone="muted">
          Vous choisissez un prestataire de paiement externe. Ses propres
          conditions et frais s’appliquent. souha.it conserve seulement la
          promesse et son état pour que le groupe puisse se coordonner.
        </Text>
      </section>
      <CommercialFooter navigate={navigate} />
    </main>
  );
};

export const LegalPage: FC<{
  navigate: Navigate;
  kind: 'privacy' | 'terms';
}> = ({navigate, kind}) => {
  useEffect(() => {
    document.title = `${
      kind === 'privacy' ? 'Confidentialité' : 'Conditions d’utilisation'
    } | souha.it`;
  }, [kind]);
  return (
    <main className="commercial-page legal-page">
      <CommercialHeader navigate={navigate} />
      <article className="legal-copy">
        <Text size="sm" tone="muted">Mis à jour le 31 août 2026</Text>
        <Heading display level={1}>
          {kind === 'privacy'
            ? 'Vos souhaits restent des données personnelles, pas un produit.'
            : 'Des règles simples pour offrir ensemble.'}
        </Heading>
        {kind === 'privacy' ? (
          <>
            <Heading level={2}>Données traitées</Heading>
            <Text>
              souha.it traite l’e-mail du propriétaire, le contenu des listes,
              les sessions, les promesses, les réservations et les messages
              nécessaires au service. Les pages publiques n’exposent jamais le
              nom, l’e-mail, le montant individuel ou la note privée d’un
              contributeur.
            </Text>
            <Heading level={2}>Finalités et base</Heading>
            <Text>
              Ces données servent à fournir le service demandé, sécuriser les
              accès, prévenir les abus, envoyer les messages transactionnels et
              facturer les offres choisies. Les événements produit sont
              pseudonymes, limités à une liste fermée et ne contiennent aucun
              texte privé.
            </Text>
            <Heading level={2}>Prestataires</Heading>
            <Text>
              Cloudflare héberge l’application, la base et les e-mails
              transactionnels. Lemon Squeezy facture uniquement les offres
              souha.it en qualité de marchand officiel. Un lien de paiement
              configuré par un propriétaire ouvre un site tiers choisi par lui ;
              ce tiers applique sa propre politique.
            </Text>
            <Heading level={2}>Durées et droits</Heading>
            <Text>
              Les sessions propriétaire expirent après 30 jours, les codes de
              connexion après 10 minutes et les messages de capitaine après 90
              jours. Vous pouvez demander accès, rectification, export ou
              suppression à privacy@souha.it. Une demande peut nécessiter une
              vérification d’identité proportionnée.
            </Text>
          </>
        ) : (
          <>
            <Heading level={2}>Rôle de souha.it</Heading>
            <Text>
              souha.it fournit un outil de liste et de coordination. Le service
              ne vend pas les cadeaux, ne garantit pas leur disponibilité et
              ne reçoit, ne conserve ni ne transfère l’argent destiné aux
              cadeaux.
            </Text>
            <Heading level={2}>Vos responsabilités</Heading>
            <Text>
              Vous devez publier des contenus licites, utiliser des liens que
              vous êtes autorisé à partager et décrire honnêtement le cadeau et
              son prix. Les promesses ne constituent pas un contrat de vente.
              Le groupe reste responsable du paiement externe, de l’achat, de
              la livraison, des remboursements et des éventuels litiges.
            </Text>
            <Heading level={2}>Offres payantes</Heading>
            <Text>
              Le Pass occasion est valable 12 mois pour la liste choisie. Plus
              se renouvelle mensuellement ou annuellement jusqu’à annulation
              depuis le portail Lemon Squeezy. Les droits légaux de rétractation
              et de remboursement applicables restent inchangés.
            </Text>
            <Heading level={2}>Sécurité et suspension</Heading>
            <Text>
              Les accès sont personnels. souha.it peut limiter ou suspendre un
              usage frauduleux, illicite, trompeur, automatisé ou dangereux pour
              d’autres utilisateurs. Les signalements et questions sont reçus
              par écrit à support@souha.it.
            </Text>
          </>
        )}
        <Paper variant="tinted" className="legal-contact">
          <Heading level={2}>Une question écrite ?</Heading>
          <Text tone="muted">
            Confidentialité : privacy@souha.it · Service : support@souha.it
          </Text>
        </Paper>
      </article>
      <CommercialFooter navigate={navigate} />
    </main>
  );
};
